import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from 'api/axiosBaseQuery';
import {
  JobDetail,
  ReviewFilterOptions,
  ReviewQueueParams,
  ReviewQueueResponse,
  SubmitReviewRequest,
  SubmitReviewResponse,
} from './types';

const BASE = 'documentReview';

const toQueryString = (params: ReviewQueueParams & { uid: string }): string => {
  const search = new URLSearchParams();
  search.set('uid', params.uid);
  if (params.reviewStatus) search.set('reviewStatus', params.reviewStatus);
  if (params.extractionTypeKey != null) {
    search.set('extractionTypeKey', String(params.extractionTypeKey));
  }
  if (params.mrn) search.set('mrn', params.mrn);
  if (params.clientKey) search.set('clientKey', params.clientKey);
  if (params.sort) search.set('sort', params.sort);
  if (params.page != null) search.set('page', String(params.page));
  if (params.pageSize != null) search.set('pageSize', String(params.pageSize));
  const qs = search.toString();
  return qs ? `?${qs}` : '';
};

// Every call carries `uid`, the impersonation target, as the API requires.
export const documentReviewApi = createApi({
  reducerPath: 'documentReviewApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['ReviewQueue', 'JobDetail'],
  endpoints: builder => ({
    getReviewQueue: builder.query<
      ReviewQueueResponse,
      ReviewQueueParams & { uid: string }
    >({
      query: params => ({
        url: `${BASE}${toQueryString(params)}`,
        method: 'GET',
      }),
      providesTags: [{ type: 'ReviewQueue', id: 'LIST' }],
    }),

    getReviewFilterOptions: builder.query<ReviewFilterOptions, { uid: string }>({
      query: ({ uid }) => ({
        url: `${BASE}/filters?uid=${encodeURIComponent(uid)}`,
        method: 'GET',
      }),
    }),

    getJobDetail: builder.query<JobDetail, { jobKey: number; uid: string }>({
      query: ({ jobKey, uid }) => ({
        url: `${BASE}/${jobKey}?uid=${encodeURIComponent(uid)}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { jobKey }) => [
        { type: 'JobDetail', id: jobKey },
      ],
    }),

    // Refreshes the queue (status and count) and this job's detail.
    submitReview: builder.mutation<
      SubmitReviewResponse,
      { jobKey: number; uid: string; request: SubmitReviewRequest }
    >({
      query: ({ jobKey, uid, request }) => ({
        url: `${BASE}/${jobKey}/review?uid=${encodeURIComponent(uid)}`,
        method: 'POST',
        data: request,
      }),
      invalidatesTags: (_result, _error, { jobKey }) => [
        { type: 'ReviewQueue', id: 'LIST' },
        { type: 'JobDetail', id: jobKey },
      ],
    }),
  }),
});

export const {
  useGetReviewQueueQuery,
  useGetReviewFilterOptionsQuery,
  useGetJobDetailQuery,
  useSubmitReviewMutation,
} = documentReviewApi;
